import React, {useEffect, useMemo, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {logout as apiLogout} from '../../utils/authHelper'
import {clearSessionUser, readSessionUser} from '../../utils/sessionHelper'
import {BACKEND_URL} from '../../utils/loadEnv'
import './MainPage.css'

type InvoicesLoadState = 'idle' | 'loading' | 'loaded' | 'error'
type IssuerFilter = 'all' | 'verlag' | 'gmbh'
type PaidFilter = 'all' | 'paid' | 'unpaid'
type SortKey = 'id' | 'author' | 'recipient' | 'paid' | 'order_id' | 'invoice_date'
type SortDir = 'asc' | 'desc'

type InvoiceListItem = {
    id: number
    order_id: string | null
    order_date: string | null
    invoice_date: string | null
    author: string
    recipient: string
    paid: boolean
}

const getYearShort = () => {
    const year = new Date().getFullYear() % 100
    return String(year).padStart(2, '0')
}

const formatInvoiceNumber = (issuer: 'verlag' | 'gmbh', id: number) => {
    const prefix = issuer === 'verlag' ? 'V' : 'G'
    const yearShort = getYearShort()
    const seq = String(id).padStart(4, '0')
    return `${prefix}-${yearShort}-${seq}`
}

const issuerFromAuthor = (author: string) => {
    const a = author.toLowerCase()
    return a.includes('familienkost') ? 'gmbh' : 'verlag'
}

const extractListInvoicesFromResponse = (raw: unknown): { success: boolean; message: string; data: any[] } => {
    const body =
        raw && typeof raw === 'object' && 'body' in raw && (raw as any).body && typeof (raw as any).body === 'object'
            ? (raw as any).body
            : raw

    const b = body as any

    return {
        success: Boolean(b?.success),
        message: typeof b?.message === 'string' ? b.message : '',
        data: Array.isArray(b?.data) ? b.data : []
    }
}

const normalizeInvoices = (items: any[]): InvoiceListItem[] => {
    return items
        .filter((x) => x && typeof x === 'object')
        .map((x) => {
            const id = Number((x as any).id)
            const order_id = (x as any).order_id == null ? null : String((x as any).order_id)
            const order_date = (x as any).order_date == null ? null : String((x as any).order_date)
            const invoice_date = (x as any).invoice_date == null ? null : String((x as any).invoice_date)
            const author = String((x as any).author ?? '')
            const recipient = String((x as any).recipient ?? '')
            const paid = Boolean((x as any).paid)

            return {id, order_id, order_date, invoice_date, author, recipient, paid}
        })
        .filter((x) => Number.isFinite(x.id) && x.id > 0)
}

const dateKeyFromGerman = (s: string | null): number => {
    if (!s) return -1
    const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(s.trim())
    if (!m) return -1
    const dd = Number(m[1])
    const mm = Number(m[2])
    const yyyy = Number(m[3])
    if (!Number.isFinite(dd) || !Number.isFinite(mm) || !Number.isFinite(yyyy)) return -1
    return yyyy * 10000 + mm * 100 + dd
}

const MainPage: React.FC = () => {
    const user = readSessionUser()
    const navigate = useNavigate()

    const [invoicesLoadState, setInvoicesLoadState] = useState<InvoicesLoadState>('idle')
    const [invoicesError, setInvoicesError] = useState('')
    const [invoices, setInvoices] = useState<InvoiceListItem[]>([])

    const [search, setSearch] = useState('')
    const [issuerFilter, setIssuerFilter] = useState<IssuerFilter>('all')
    const [paidFilter, setPaidFilter] = useState<PaidFilter>('all')
    const [authorFilter, setAuthorFilter] = useState<string>('all')

    const [sortKey, setSortKey] = useState<SortKey>('id')
    const [sortDir, setSortDir] = useState<SortDir>('desc')

    const loadInvoices = async (signal?: AbortSignal) => {
        if (!BACKEND_URL || BACKEND_URL.trim().length === 0) {
            setInvoicesLoadState('error')
            setInvoicesError('BACKEND_URL fehlt (REACT_APP_BACKEND_URL).')
            return
        }

        setInvoicesLoadState('loading')
        setInvoicesError('')

        try {
            const res = await fetch(`${BACKEND_URL}/invoices/all-invoices`, {
                method: 'GET',
                credentials: 'include',
                signal,
                headers: {Accept: 'application/json'}
            })

            const raw = await res.json()
            const extracted = extractListInvoicesFromResponse(raw)

            if (!res.ok || !extracted.success) {
                throw new Error(extracted.message || `HTTP ${res.status}`)
            }

            const normalized = normalizeInvoices(extracted.data)
            setInvoices(normalized)
            setInvoicesLoadState('loaded')
        } catch (err) {
            if ((err as any)?.name === 'AbortError') return
            setInvoicesLoadState('error')
            setInvoicesError((err as Error).message || 'Rechnungen konnten nicht geladen werden')
        }
    }

    useEffect(() => {
        const controller = new AbortController()
        loadInvoices(controller.signal)
        return () => controller.abort()
    }, [])

    const authorOptions = useMemo(() => {
        const set = new Set<string>()
        for (const inv of invoices) {
            const a = inv.author.trim()
            if (a.length > 0) set.add(a)
        }
        return Array.from(set).sort((a, b) => a.localeCompare(b, 'de'))
    }, [invoices])

    const decorated = useMemo(() => {
        return invoices.map((inv) => {
            const issuer = issuerFromAuthor(inv.author)
            const invoice_number = formatInvoiceNumber(issuer, inv.id)
            return {...inv, issuer, invoice_number}
        })
    }, [invoices])

    const filteredInvoices = useMemo(() => {
        let list = [...decorated]

        const q = search.trim().toLowerCase()
        if (q.length > 0) {
            list = list.filter((x) => {
                const hay = `${x.invoice_number} ${x.id} ${x.order_id ?? ''} ${x.author} ${x.recipient}`.toLowerCase()
                return hay.includes(q)
            })
        }

        if (issuerFilter !== 'all') {
            list = list.filter((x) => x.issuer === issuerFilter)
        }

        if (paidFilter !== 'all') {
            list = list.filter((x) => (paidFilter === 'paid' ? x.paid : !x.paid))
        }

        if (authorFilter !== 'all') {
            list = list.filter((x) => x.author === authorFilter)
        }

        const cmpString = (a: string, b: string) => a.localeCompare(b, 'de')
        const cmpNum = (a: number, b: number) => a - b

        list.sort((a, b) => {
            if (sortKey === 'paid') {
                const av = a.paid ? 1 : 0
                const bv = b.paid ? 1 : 0
                if (av !== bv) return av - bv
                return cmpNum(a.id, b.id)
            }

            if (sortKey === 'id') return cmpNum(a.id, b.id)
            const cmpNullableString = (a: string | null, b: string | null) => cmpString(a ?? '', b ?? '')
            if (sortKey === 'order_id') return cmpNullableString(a.order_id, b.order_id)
            if (sortKey === 'invoice_date') return cmpNum(dateKeyFromGerman(a.invoice_date), dateKeyFromGerman(b.invoice_date))
            if (sortKey === 'author') return cmpString(a.author, b.author)
            return cmpString(a.recipient, b.recipient)
        })

        if (sortDir === 'desc') list.reverse()

        return list
    }, [decorated, search, issuerFilter, paidFilter, authorFilter, sortKey, sortDir])

    const toggleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'))
            return
        }
        setSortKey(key)
        setSortDir(key === 'id' ? 'desc' : 'asc')
    }

    const sortMark = (key: SortKey) => {
        if (sortKey !== key) return ''
        return sortDir === 'asc' ? ' ▲' : ' ▼'
    }

    const resetFilters = () => {
        setSearch('')
        setIssuerFilter('all')
        setPaidFilter('all')
        setAuthorFilter('all')
        setSortKey('id')
        setSortDir('desc')
    }

    const doLogout = async () => {
        await apiLogout()
        clearSessionUser()
        window.location.href = '/login'
    }

    const openInvoice = (id: number) => {
        navigate(`/editingpage/${id}`)
    }

    return (
        <main className='mainPage'>
            <div className='mainCard'>
                <img
                    className='mainLogo'
                    src='https://www.familienkost.de/icon-svgs/logos/Familienkost_Logo_Horizontal_Farbig.svg'
                    alt='Familienkost Logo'
                />

                {user && (
                    <div className='mainUserRow'>
                        <img
                            className='mainAvatar'
                            src={user.image_url}
                            alt={`${user.firstname} ${user.lastname}`}
                        />

                        <div className='mainUserInfo'>
                            <div className='mainUserName'>
                                {user.firstname} {user.lastname}
                            </div>
                            <div className='mainUserRole'>
                                {user.role}
                            </div>
                        </div>

                        <button
                            type='button'
                            className='mainButton mainButtonGhost'
                            onClick={doLogout}
                        >
                            Abmelden
                        </button>
                    </div>
                )}

                <div className='mainDashboard'>
                    <div className='mainDashboardHeader'>
                        <h2>Dashboard</h2>

                        <div className='mainDashboardActions'>
                            <button
                                type='button'
                                className='mainDashboardReminder'
                                onClick={() => navigate('payment-reminders')}
                            >
                                Zahlungserinnerung
                            </button>

                            <button
                                type='button'
                                className='mainDashboardPlus'
                                onClick={() => navigate('creationpage')}
                            >
                                +
                            </button>
                        </div>
                    </div>

                    <div className='invoicesToolbar'>
                        <div className='invoicesSearchWrap'>
                            <input
                                className='invoicesSearch'
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder='Suche nach Rechnungsnummer, ID, Order ID, Autor oder Empfänger…'
                            />

                            <button
                                type='button'
                                className='invoicesMiniButton'
                                onClick={() => setSearch('')}
                                disabled={search.trim().length === 0}
                                title='Suche leeren'
                            >
                                ×
                            </button>
                        </div>

                        <div className='invoicesFilters'>
                            <select
                                className='invoicesSelect'
                                value={issuerFilter}
                                onChange={(e) => setIssuerFilter(e.target.value as IssuerFilter)}
                                title='Aussteller filtern'
                            >
                                <option value='all'>Alle Aussteller</option>
                                <option value='verlag'>Verlag</option>
                                <option value='gmbh'>GmbH</option>
                            </select>

                            <select
                                className='invoicesSelect'
                                value={paidFilter}
                                onChange={(e) => setPaidFilter(e.target.value as PaidFilter)}
                                title='Status filtern'
                            >
                                <option value='all'>Alle Status</option>
                                <option value='unpaid'>Offen</option>
                                <option value='paid'>Bezahlt</option>
                            </select>

                            <button
                                type='button'
                                className='invoicesReset'
                                onClick={resetFilters}
                                title='Filter zurücksetzen'
                            >
                                Filter entfernen
                            </button>
                        </div>

                        <div className='invoicesMetaRow'>
                            <div className='invoicesCount'>
                                {filteredInvoices.length} von {decorated.length} Rechnungen
                            </div>
                            <div className='invoicesSortHint'>
                                Klick auf Spaltenkopf zum Sortieren
                            </div>
                        </div>
                    </div>

                    {invoicesLoadState === 'loading' && (
                        <div className='invoicesHintBlock'>Rechnungen werden geladen…</div>
                    )}

                    {invoicesLoadState === 'error' && (
                        <div className='invoicesErrorBlock'>
                            {invoicesError}
                        </div>
                    )}

                    {invoicesLoadState === 'loaded' && filteredInvoices.length === 0 && (
                        <div className='invoicesEmptyBlock'>
                            Keine Rechnungen gefunden. Passe Suche/Filter an oder erstelle eine neue Rechnung.
                        </div>
                    )}

                    {invoicesLoadState === 'loaded' && filteredInvoices.length > 0 && (
                        <div className='invoiceTableWrap'>
                            <table className='invoiceTable'>
                                <thead>
                                <tr>
                                    <th
                                        className='invoiceTh invoiceThMedium invoiceThSortable'
                                        onClick={() => toggleSort('id')}
                                    >
                                        Rechnung{sortMark('id')}
                                    </th>
                                    <th
                                        className='invoiceTh invoiceThLarger invoiceThSortable'
                                        onClick={() => toggleSort('order_id')}
                                    >
                                        Order-ID{sortMark('order_id')}
                                    </th>
                                    <th
                                        className='invoiceTh invoiceThSortable'
                                        onClick={() => toggleSort('invoice_date')}
                                    >
                                        Rechnungsdatum{sortMark('invoice_date')}
                                    </th>
                                    <th
                                        className='invoiceTh invoiceThSortable'
                                        onClick={() => toggleSort('author')}
                                    >
                                        Autor{sortMark('author')}
                                    </th>
                                    <th
                                        className='invoiceTh invoiceThSortable'
                                        onClick={() => toggleSort('recipient')}
                                    >
                                        Empfänger{sortMark('recipient')}
                                    </th>
                                    <th
                                        className='invoiceTh invoiceThSortable invoiceThPaid'
                                        onClick={() => toggleSort('paid')}
                                    >
                                        Bezahlt{sortMark('paid')}
                                    </th>
                                </tr>
                                </thead>

                                <tbody>
                                {filteredInvoices.map((inv) => (
                                    <tr
                                        key={inv.id}
                                        className='invoiceRow invoiceRowClickable'
                                        onClick={() => openInvoice(inv.id)}
                                        title='Zum Bearbeiten öffnen'
                                    >
                                        <td className='invoiceTd'>
                                            <div className='invoiceNumberCell'>

                                                <span className='invoiceNumberPill'>
                                                    {inv.invoice_number}
                                                </span>
                                            </div>
                                        </td>

                                        <td className='invoiceTd invoiceTdMuted'>
                                            {inv.order_id == null ? '—' : String(inv.order_id)}
                                        </td>

                                        <td className='invoiceTd invoiceTdMuted'>
                                            {inv.invoice_date ?? '—'}
                                        </td>

                                        <td className='invoiceTd invoiceTdMuted'>
                                            {inv.author === 'Terence Böhme' ? 'Verlag Terence Böhme' : 'Familienkost GmbH'}
                                        </td>

                                        <td className='invoiceTd invoiceTdMuted' title={inv.recipient}>
                                            {inv.recipient}
                                        </td>

                                        <td
                                            className='invoiceTd invoiceTdPaid'
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <label className='paidCheck'>
                                                <input
                                                    className='paidCheckbox'
                                                    type='checkbox'
                                                    checked={inv.paid}
                                                    onChange={async () => {
                                                        const nextPaid = !inv.paid

                                                        const res = await fetch(`${BACKEND_URL}/invoices/mark-invoice-as-paid`, {
                                                            method: 'POST',
                                                            credentials: 'include',
                                                            headers: {
                                                                Accept: 'application/json',
                                                                'Content-Type': 'application/json'
                                                            },
                                                            body: JSON.stringify({
                                                                invoice_id: inv.id,
                                                                paid: nextPaid
                                                            })
                                                        })

                                                        const raw = await res.json()
                                                        const extracted = extractListInvoicesFromResponse(raw)

                                                        if (!res.ok || !extracted.success) {
                                                            throw new Error(extracted.message || `HTTP ${res.status}`)
                                                        }

                                                        setInvoices((prev) =>
                                                            prev.map((x) => (x.id === inv.id ? {
                                                                ...x,
                                                                paid: nextPaid
                                                            } : x))
                                                        )
                                                    }}
                                                    title={inv.paid ? 'Bezahlt' : 'Offen'}
                                                />
                                            </label>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <footer className='mainFooter'>
                    © Familienkost ·{' '}
                    {new Date().toLocaleDateString('de-DE', {
                        month: 'long',
                        year: 'numeric'
                    })}
                </footer>
            </div>
        </main>
    )
}

export default MainPage
